import React, { useEffect, useRef, useState } from 'react';
import { firstEntryId } from './tree.js';
import { useHoverGlide } from './glide.jsx';

export function Sidebar({ tree, activeId, openKeys, onSelect, badgesFor }) {
  const [expanded, setExpanded] = useState(() => new Set(openKeys));
  const navRef = useRef(null);
  const scrollRef = useRef(null);
  const glowRef = useRef(null);
  useHoverGlide(scrollRef, glowRef, '.side-row, .side-story');

  // Open the active entry's ancestors when the selection changes elsewhere.
  useEffect(() => {
    if (!openKeys.length) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const k of openKeys) next.add(k);
      return next;
    });
  }, [openKeys.join('|')]);

  useEffect(() => {
    const el = navRef.current?.querySelector('[aria-current="page"]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <nav className="shell-side" aria-label="Components" ref={navRef}>
      <div className="shell-side__scroll" ref={scrollRef}>
        <div ref={glowRef} className="edl-hover-glide" aria-hidden="true" />
        {tree.children.map((section) => (
          <section className="side-section" key={section.key}>
            {section.name && (
              <div className="side-section__head">
                <h2 className="edl-label side-section__label">{section.name}</h2>
                <SectionToggle keys={collapsibleKeys(section)} expanded={expanded} setExpanded={setExpanded} name={section.name} />
              </div>
            )}
            <ul className="side-list">
              {(section.type === 'section' ? section.children : [section]).map((node) => (
                <TreeNode key={node.key} node={node} depth={0} expanded={expanded} toggle={toggle} activeId={activeId} onSelect={onSelect} badgesFor={badgesFor} />
              ))}
              {section.type === 'section' && section.entries?.length > 0 && (
                <TreeNode node={{ ...section, type: 'component', key: section.key + '#self' }} depth={0} expanded={expanded} toggle={toggle} activeId={activeId} onSelect={onSelect} badgesFor={badgesFor} />
              )}
            </ul>
          </section>
        ))}
      </div>
    </nav>
  );
}

// The rows in a section that open and close: folders and components with
// stories (pages are plain links).
function collapsibleKeys(section) {
  const keys = [];
  const walk = (node) => {
    if (node.type === 'folder') {
      keys.push(node.key);
      node.children.forEach(walk);
    } else if (node.entries?.some((e) => e.type === 'story')) {
      keys.push(node.key);
    }
  };
  if (section.type === 'section') {
    section.children.forEach(walk);
    if (section.entries?.length) walk({ ...section, type: 'component', key: section.key + '#self' });
  } else {
    walk(section);
  }
  return keys;
}

// Collapse every row in the section, or expand them all once none is open.
function SectionToggle({ keys, expanded, setExpanded, name }) {
  if (!keys.length) return null;
  const anyOpen = keys.some((k) => expanded.has(k));
  const label = anyOpen ? 'Collapse all' : 'Expand all';
  return (
    <button
      type="button"
      className="side-section__toggle"
      aria-label={`${label} in ${name}`}
      title={label}
      onClick={() =>
        setExpanded((prev) => {
          const next = new Set(prev);
          for (const k of keys) anyOpen ? next.delete(k) : next.add(k);
          return next;
        })
      }
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={anyOpen ? 'M7 20l5-5 5 5M7 4l5 5 5-5' : 'M7 15l5 5 5-5M7 9l5-5 5 5'} />
      </svg>
    </button>
  );
}

function TreeNode({ node, depth, expanded, toggle, activeId, onSelect, badgesFor }) {
  const open = expanded.has(node.key);

  if (node.type === 'folder') {
    return (
      <li className="side-item">
        <button type="button" className="side-row side-row--folder" aria-expanded={open} onClick={() => toggle(node.key)} style={{ '--depth': depth }}>
          <span className="side-row__toggle" aria-hidden="true">
            <Chevron open={open} />
          </span>
          <span className="side-row__name">{node.name}</span>
        </button>
        {open && (
          <ul className="side-list">
            {node.children.map((child) => (
              <TreeNode key={child.key} node={child} depth={depth + 1} expanded={expanded} toggle={toggle} activeId={activeId} onSelect={onSelect} badgesFor={badgesFor} />
            ))}
          </ul>
        )}
      </li>
    );
  }

  const stories = node.entries.filter((e) => e.type === 'story');
  const docs = node.entries.find((e) => e.type === 'docs');
  const isPage = stories.length === 0;
  const containsActive = node.entries.some((e) => e.id === activeId);
  const badges = badgesFor(docs ?? node.entries[0]);
  const target = docs?.id ?? firstEntryId(node);

  if (isPage) {
    return (
      <li className="side-item">
        <a
          href={`?path=/docs/${target}`}
          className={`side-row side-row--page${containsActive ? ' is-active' : ''}`}
          aria-current={containsActive ? 'page' : undefined}
          onClick={(e) => {
            e.preventDefault();
            onSelect(target);
          }}
          style={{ '--depth': depth }}
        >
          <span className="side-row__name">{node.name}</span>
          <Badges badges={badges} />
        </a>
      </li>
    );
  }

  return (
    <li className={`side-item${containsActive ? ' contains-active' : ''}`}>
      <div className={`side-row side-row--component${docs && docs.id === activeId ? ' is-active' : ''}${containsActive ? ' is-within' : ''}`} style={{ '--depth': depth }}>
        <button type="button" className="side-row__toggle" aria-expanded={open} aria-label={`${open ? 'Collapse' : 'Expand'} ${node.name}`} onClick={() => toggle(node.key)}>
          <Chevron open={open} />
        </button>
        <a
          href={`?path=/docs/${target}`}
          className="side-row__link"
          aria-current={docs && docs.id === activeId ? 'page' : undefined}
          onClick={(e) => {
            e.preventDefault();
            if (!open) toggle(node.key);
            onSelect(target);
          }}
        >
          <span className="side-row__name">{node.name}</span>
        </a>
        <Badges badges={badges} />
      </div>
      {open && (
        <ul className="side-list side-list--stories">
          {stories.map((s) => (
            <li key={s.id}>
              <a
                href={`?path=/story/${s.id}`}
                className={`side-story${s.id === activeId ? ' is-active' : ''}`}
                aria-current={s.id === activeId ? 'page' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  onSelect(s.id);
                }}
                style={{ '--depth': depth }}
              >
                {s.name}
              </a>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Badges({ badges }) {
  if (!badges?.length) return null;
  return (
    <span className="side-badges">
      {badges.map((b, i) => (
        <span key={i} className={`side-badge side-badge--${b.tone ?? 'outline'}${b.signal ? ` side-badge--signal-${b.signal}` : ''}`} title={b.title ?? b.label}>
          {b.short ?? b.label}
        </span>
      ))}
    </span>
  );
}

function Chevron({ open }) {
  return (
    <svg className={`side-chevron${open ? ' is-open' : ''}`} viewBox="0 0 12 12" aria-hidden="true">
      <path d="M4.5 2.5L8 6l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
